import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { beginCarouselDrag } from "../../lib/carouselDrag";

export function ExploreCarousel({ id, title, children, more, loading, failed, loadMore }: {
	id: string;
	title: string;
	children: ReactNode;
	more: boolean;
	loading: boolean;
	failed: boolean;
	loadMore: () => Promise<void>;
}) {
	const { t } = useTranslation();
	const ref = useRef<HTMLDivElement>(null);
	const [edges, setEdges] = useState({ previous: false, next: false });
	useLayoutEffect(() => {
		const row = ref.current;
		if (!row) return;
		const update = () => {
			const previous = row.scrollLeft > 2, next = row.scrollLeft + row.clientWidth < row.scrollWidth - 2;
			setEdges((current) => current.previous === previous && current.next === next ? current : { previous, next });
			if (row.scrollLeft > 0 && more && !loading && !failed) void loadMore();
		};
		const wheel = (event: WheelEvent) => {
			if (!event.shiftKey || event.ctrlKey || event.deltaX) return;
			event.preventDefault();
			row.scrollBy({ left: event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? row.clientWidth : 1) });
		};
		update();
		row.addEventListener("scroll", update, { passive: true });
		row.addEventListener("wheel", wheel, { passive: false });
		const resize = new ResizeObserver(update);
		resize.observe(row);
		if (row.firstElementChild) resize.observe(row.firstElementChild);
		return () => { row.removeEventListener("scroll", update); row.removeEventListener("wheel", wheel); resize.disconnect(); };
	}, [children, more, loading, failed, loadMore]);

	async function scroll(direction: number) {
		const row = ref.current;
		if (!row) return;
		if (direction > 0 && more && !loading) {
			const atEnd = row.scrollLeft + row.clientWidth >= row.scrollWidth - 2;
			const request = loadMore();
			if (atEnd) { await request; await new Promise<void>((resolve) => requestAnimationFrame(() => resolve())); }
		}
		if (!row.isConnected) return;
		row.scrollBy({ left: direction * row.clientWidth * .85,
			behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth" });
	}
	return <div className="explore-carousel" data-previous={edges.previous} data-next={edges.next || more}>
		<div ref={ref} id={id} className="explore-carousel-viewport" data-explore-scroll tabIndex={0} role="region" aria-label={title}
			onDragStart={(event) => event.preventDefault()}
			onPointerDown={(event) => { if (ref.current) beginCarouselDrag(event.nativeEvent, ref.current); }}
			onKeyDown={(event) => {
				if (event.target !== event.currentTarget || !["ArrowLeft", "ArrowRight"].includes(event.key)) return;
				event.preventDefault(); void scroll(event.key === "ArrowLeft" ? -1 : 1);
			}}>
			{children}
		</div>
		<button type="button" className="explore-arrow explore-arrow--previous" disabled={!edges.previous}
			onClick={() => void scroll(-1)} aria-controls={id} aria-label={t("discover.previousIn", { title })}>
			<ChevronLeft size={24} />
		</button>
		<button type="button" className="explore-arrow explore-arrow--next" disabled={!edges.next && (!more || loading)}
			onClick={() => void scroll(1)} aria-controls={id} aria-label={t("discover.nextIn", { title })}>
			<ChevronRight size={24} />
		</button>
		{loading ? <span role="status" className="sr-only">{t("discover.loadingContent")}</span> : null}
	</div>;
}
